import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Button,
  Checkbox,
  IconButton,
  InlineLoading,
  InlineNotification,
  Modal,
  Select,
  SelectItem,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  TextArea,
  TextInput,
  Toggle,
} from '@carbon/react';
import { Edit, TrashCan } from '@carbon/icons-react';
import {
  DataTable,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from 'carbon-components-react';

import Icon, { Icons } from '../../Icon/Icon';
import { useAppSelector } from '../../../app/hooks';
import { selectToken } from '../../../AppSlice';
import { GatewayIp } from '../../../data/endpoints';

type ResourceType = 'FILESYSTEM' | 'S3' | 'SDI' | 'NDI' | 'SRT' | 'RTMP' | 'FTP' | 'SFTP' | 'HTTP';
type ResourceDirection = 'SOURCE' | 'TARGET' | 'BOTH';
type ResourceCapability =
  | 'FILE_BASED_READ'
  | 'FILE_BASED_WRITE'
  | 'LIVE_INPUT'
  | 'LIVE_OUTPUT'
  | 'WATCHABLE'
  | 'RECORDABLE_SOURCE'
  | 'RECORDABLE_TARGET'
  | 'TRANSCODE_INPUT'
  | 'TRANSCODE_OUTPUT';

type ResourceRecord = {
  id: string;
  tenantId: string | null;
  label: string;
  resourceType: ResourceType;
  direction: ResourceDirection;
  enabled: boolean;
  temporary: boolean;
  description?: string | null;
  authRefId?: string | null;
  config?: Record<string, any> | null;
  capabilities?: ResourceCapability[] | null;
  createdAt?: string;
  updatedAt?: string;
};

type ResourceDraft = {
  label: string;
  resourceType: ResourceType;
  directionSource: boolean;
  directionTarget: boolean;
  enabled: boolean;
  description: string;
  capabilities: ResourceCapability[];
  config: Record<string, string>;
};

type ConfigField = {
  key: string;
  label: string;
  placeholder?: string;
  type?: 'text' | 'password' | 'number';
  multiline?: boolean;
  fullWidth?: boolean;
};

const resourceTypeTabs: Array<{ id: ResourceType; label: string }> = [
  { id: 'FILESYSTEM', label: 'Filesystem' },
  { id: 'S3', label: 'S3' },
  { id: 'SDI', label: 'SDI' },
  { id: 'NDI', label: 'NDI' },
  { id: 'SRT', label: 'SRT' },
  { id: 'RTMP', label: 'RTMP' },
  { id: 'FTP', label: 'FTP' },
  { id: 'SFTP', label: 'SFTP' },
  { id: 'HTTP', label: 'HTTP' },
];

const capabilityOptions: Array<{ id: ResourceCapability; label: string }> = [
  { id: 'FILE_BASED_READ', label: 'File Based Read' },
  { id: 'FILE_BASED_WRITE', label: 'File Based Write' },
  { id: 'LIVE_INPUT', label: 'Live Input' },
  { id: 'LIVE_OUTPUT', label: 'Live Output' },
  { id: 'WATCHABLE', label: 'Watchable' },
  { id: 'RECORDABLE_SOURCE', label: 'Recordable Source' },
  { id: 'RECORDABLE_TARGET', label: 'Recordable Target' },
  { id: 'TRANSCODE_INPUT', label: 'Transcode Input' },
  { id: 'TRANSCODE_OUTPUT', label: 'Transcode Output' },
];

const resourceTypeConfigFields: Record<ResourceType, ConfigField[]> = {
  FILESYSTEM: [
    { key: 'path', label: 'Path', placeholder: '/mnt/files/somelocation', fullWidth: true },
  ],
  S3: [
    { key: 'bucket', label: 'Bucket', placeholder: 'media-bucket' },
    { key: 'region', label: 'Region', placeholder: 'eu-central-1' },
    { key: 'keyPrefix', label: 'Key Prefix', placeholder: 'resources/' },
    { key: 'endpointUrl', label: 'Endpoint URL', placeholder: 'https://s3.amazonaws.com' },
    { key: 'accessKey', label: 'Access Key' },
    { key: 'secretKey', label: 'Secret Key', type: 'password' },
    { key: 'url', label: 'URL', placeholder: 's3://media-bucket/resources', fullWidth: true },
  ],
  SDI: [
    { key: 'deviceIndex', label: 'Device Index', placeholder: '4', type: 'number' },
    { key: 'url', label: 'URL', placeholder: 'sdi://DeckLinkDevice(4)' },
  ],
  NDI: [
    { key: 'sourceName', label: 'Source Name', placeholder: 'Studio Camera A' },
    { key: 'group', label: 'Group', placeholder: 'Default' },
    { key: 'url', label: 'URL', placeholder: 'ndi://Studio Camera A', fullWidth: true },
  ],
  SRT: [
    { key: 'host', label: 'Host', placeholder: '239.1.1.10' },
    { key: 'port', label: 'Port', placeholder: '9000', type: 'number' },
    { key: 'streamId', label: 'Stream ID', placeholder: 'channel-1' },
    { key: 'mode', label: 'Mode', placeholder: 'listener' },
    { key: 'url', label: 'URL', placeholder: 'srt://239.1.1.10:9000?streamid=channel-1', fullWidth: true },
  ],
  RTMP: [
    { key: 'serverUrl', label: 'Server URL', placeholder: 'rtmp://server/live' },
    { key: 'streamKey', label: 'Stream Key', placeholder: 'stream-key' },
    { key: 'url', label: 'URL', placeholder: 'rtmp://server/live/stream-key', fullWidth: true },
  ],
  FTP: [
    { key: 'host', label: 'Host', placeholder: 'ftp.example.com' },
    { key: 'port', label: 'Port', placeholder: '21', type: 'number' },
    { key: 'username', label: 'Username' },
    { key: 'password', label: 'Password', type: 'password' },
    { key: 'path', label: 'Path', placeholder: '/incoming', fullWidth: true },
    // { key: 'url', label: 'URL', placeholder: 'ftp://ftp.example.com/incoming' },
  ],
  SFTP: [
    { key: 'host', label: 'Host', placeholder: 'sftp.example.com' },
    { key: 'port', label: 'Port', placeholder: '22', type: 'number' },
    { key: 'username', label: 'Username' },
    { key: 'password', label: 'Password', type: 'password' },
    { key: 'path', label: 'Path', placeholder: '/incoming' },
    { key: 'url', label: 'URL', placeholder: 'sftp://sftp.example.com/incoming' },
  ],
  HTTP: [
    { key: 'url', label: 'URL', placeholder: 'https://cdn.example.com/source.m3u8', fullWidth: true },
    { key: 'method', label: 'Method', placeholder: 'GET', fullWidth: true },
    { key: 'headers', label: 'Headers', placeholder: 'Authorization: Bearer ...', multiline: true, fullWidth: true },
  ],
};

const headers = [
  { key: 'label', header: 'Label' },
  { key: 'direction', header: 'Direction' },
  { key: 'enabled', header: 'Enabled' },
  { key: 'url', header: 'URL' },
  { key: 'capabilities', header: 'Capabilities' },
  { key: 'actions', header: '' },
];

const capabilityIconMap: Record<ResourceCapability, { icon: keyof typeof Icons; color: string; label: string }> = {
  FILE_BASED_READ: { icon: 'file', color: '#78a9ff', label: 'File-based read' },
  FILE_BASED_WRITE: { icon: 'save', color: '#24a148', label: 'File-based write' },
  LIVE_INPUT: { icon: 'control_play', color: '#24a148', label: 'Live input' },
  LIVE_OUTPUT: { icon: 'send', color: '#0f62fe', label: 'Live output' },
  WATCHABLE: { icon: 'watchfolder', color: '#8a3ffc', label: 'Watchable' },
  RECORDABLE_SOURCE: { icon: 'circle', color: '#da1e28', label: 'Recordable source' },
  RECORDABLE_TARGET: { icon: 'download', color: '#ff832b', label: 'Recordable target' },
  TRANSCODE_INPUT: { icon: 'received', color: '#42be65', label: 'Transcode input' },
  TRANSCODE_OUTPUT: { icon: 'synchronize', color: '#4589ff', label: 'Transcode output' },
};

const directionFromFlags = (source: boolean, target: boolean): ResourceDirection => {
  if (source && target) return 'BOTH';
  if (target) return 'TARGET';
  return 'SOURCE';
};

const flagsFromDirection = (direction: ResourceDirection): Pick<ResourceDraft, 'directionSource' | 'directionTarget'> => ({
  directionSource: direction === 'SOURCE' || direction === 'BOTH',
  directionTarget: direction === 'TARGET' || direction === 'BOTH',
});

const deriveConfigWithDefaults = (resourceType: ResourceType, config?: Record<string, any> | null): Record<string, string> => {
  const fields = resourceTypeConfigFields[resourceType];
  const next: Record<string, string> = {};

  fields.forEach((field) => {
    next[field.key] = (config?.[field.key] ?? '').toString();
  });

  if (config?.url && !next.url) {
    next.url = config.url.toString();
  }

  if (config?.path && !next.path) {
    next.path = config.path.toString();
  }

  return next;
};

const normalizeConfigForType = (resourceType: ResourceType, config: Record<string, string>): Record<string, string> => {
  const next = { ...config };

  if (resourceType === 'FILESYSTEM' && next.path) {
    next.path = next.path.trim();
  }

  if (resourceType === 'SDI' && next.deviceIndex && !next.url.trim()) {
    next.url = `sdi://DeckLinkDevice(${next.deviceIndex.trim()})`;
  }

  if (resourceType === 'RTMP' && next.serverUrl && next.streamKey && !next.url.trim()) {
    next.url = `${next.serverUrl.replace(/\/$/, '')}/${next.streamKey}`;
  }

  if ((resourceType === 'FTP' || resourceType === 'SFTP') && next.host && next.path && !next.url.trim()) {
    const scheme = resourceType.toLowerCase();
    next.url = `${scheme}://${next.host}${next.path.startsWith('/') ? next.path : `/${next.path}`}`;
  }

  return next;
};

const getApiErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (axios.isAxiosError(error)) {
    const apiMessage = error.response?.data?.message;
    if (typeof apiMessage === 'string' && apiMessage.trim().length > 0) {
      return apiMessage;
    }
  }

  return fallbackMessage;
};

const emptyDraft = (resourceType: ResourceType): ResourceDraft => ({
  label: '',
  resourceType,
  directionSource: true,
  directionTarget: false,
  enabled: true,
  description: '',
  capabilities: [],
  config: deriveConfigWithDefaults(resourceType, {}),
});

const toDraft = (resource: ResourceRecord): ResourceDraft => ({
  label: resource.label ?? '',
  resourceType: resource.resourceType,
  ...flagsFromDirection(resource.direction),
  enabled: resource.enabled,
  description: resource.description ?? '',
  capabilities: resource.capabilities ?? [],
  config: deriveConfigWithDefaults(resource.resourceType, resource.config ?? {}),
});

const ResourcesTab: React.FC = () => {
  const token = useAppSelector(selectToken);
  const [activeType, setActiveType] = useState<ResourceType>('FILESYSTEM');
  const [resources, setResources] = useState<ResourceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [resourceModalOpen, setResourceModalOpen] = useState(false);
  const [resourceDraft, setResourceDraft] = useState<ResourceDraft>(emptyDraft('FILESYSTEM'));
  const [editingResourceId, setEditingResourceId] = useState<string | null>(null);
  const [deleteResource, setDeleteResource] = useState<ResourceRecord | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [activeType]);

  const loadResources = async () => {
    if (!token) {
      setResources([]);
      setError('Authentication token is missing.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${GatewayIp}/api/resources`, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = response.data;
      const items: ResourceRecord[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.content)
            ? data.content
            : [];

      setResources(items);
    } catch (err) {
      setError('Failed to load resources.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResources();
  }, [token]);

  const filteredResources = useMemo(
    () => resources.filter((resource) => (resource.resourceType ?? '').toString().toUpperCase() === activeType),
    [activeType, resources]
  );

  const pagedResources = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return filteredResources.slice(startIndex, startIndex + pageSize);
  }, [filteredResources, page, pageSize]);

  const openCreateModal = () => {
    setEditingResourceId(null);
    setResourceDraft(emptyDraft(activeType));
    setResourceModalOpen(true);
  };

  const openEditModal = async (resourceId: string) => {
    if (!token) {
      setError('Authentication token is missing.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await axios.get(`${GatewayIp}/api/resources/${resourceId}`, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const resource = response.data as ResourceRecord;
      setEditingResourceId(resource.id);
      setResourceDraft(toDraft(resource));
      setResourceModalOpen(true);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load resource details.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveResource = async () => {
    if (!token) {
      setError('Authentication token is missing.');
      return;
    }

    const normalizedConfig = normalizeConfigForType(resourceDraft.resourceType, resourceDraft.config);
    const direction = directionFromFlags(resourceDraft.directionSource, resourceDraft.directionTarget);
    const hasAnyDirection = resourceDraft.directionSource || resourceDraft.directionTarget;
    const configFields = resourceTypeConfigFields[resourceDraft.resourceType];
    const requiredPrimaryField = configFields.some((field) => normalizedConfig[field.key]?.trim());

    if (!resourceDraft.label.trim() || !hasAnyDirection || !requiredPrimaryField) {
      setError('Label, direction, and resource configuration are required.');
      return;
    }

    setSubmitting(true);
    setError(null);

    const payload = {
      label: resourceDraft.label.trim(),
      resourceType: resourceDraft.resourceType,
      direction,
      enabled: resourceDraft.enabled,
      description: resourceDraft.description.trim(),
      capabilities: resourceDraft.capabilities,
      config: Object.entries(normalizedConfig).reduce<Record<string, string>>((acc, [key, value]) => {
        if (value.trim().length > 0) {
          acc[key] = value.trim();
        }
        return acc;
      }, {}),
    };

    try {
      if (editingResourceId) {
        await axios.put(`${GatewayIp}/api/resources/${editingResourceId}`, payload, {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      } else {
        await axios.post(`${GatewayIp}/api/resources`, payload, {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }

      setResourceModalOpen(false);
      await loadResources();
    } catch (err) {
      setError(getApiErrorMessage(err, editingResourceId ? 'Failed to update resource.' : 'Failed to create resource.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteResource = async () => {
    if (!token || !deleteResource) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await axios.delete(`${GatewayIp}/api/resources/${deleteResource.id}`, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      setDeleteResource(null);
      await loadResources();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to delete resource.'));
    } finally {
      setSubmitting(false);
    }
  };

  const rows = useMemo(
    () =>
      pagedResources.map((resource) => ({
        id: resource.id,
        label: resource.label ?? '—',
        direction: resource.direction ?? '—',
        enabled: (
          <span
            title={resource.enabled ? 'Enabled' : 'Disabled'}
            style={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <Icon
              type={resource.enabled ? 'check' : 'close'}
              color={resource.enabled ? '#24a148' : '#da1e28'}
              size={16}
            />
          </span>
        ),
        url: resource.resourceType === 'FILESYSTEM'
          ? resource.config?.path ?? '—'
          : resource.config?.url ?? '—',
        capabilities: Array.isArray(resource.capabilities) && resource.capabilities.length > 0
          ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {resource.capabilities.map((capability) => {
                const config = capabilityIconMap[capability];
                if (!config) return null;

                return (
                  <span key={`${resource.id}-${capability}`} title={config.label} style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <Icon type={config.icon} color={config.color} size={16} />
                  </span>
                );
              })}
            </div>
          )
          : '—',
        actions: (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
            <IconButton kind="ghost" size="sm" label="Edit resource" onClick={() => openEditModal(resource.id)}>
              <Edit />
            </IconButton>
            <IconButton kind="ghost" size="sm" label="Delete resource" onClick={() => setDeleteResource(resource)}>
              <TrashCan />
            </IconButton>
          </div>
        ),
      })),
    [pagedResources]
  );

  const selectedIndex = useMemo(
    () => Math.max(resourceTypeTabs.findIndex((tab) => tab.id === activeType), 0),
    [activeType]
  );

  const resourceConfigFields = resourceTypeConfigFields[resourceDraft.resourceType];

  return (
    <>
      <div className="settings-page__panel settings-page__resources">
        <div className="settings-page__panel-header">
          <div>
            <h3>Resources</h3>
            <p>Browse registered sources and targets by resource type.</p>
          </div>
          <Button size="sm" kind="primary" onClick={openCreateModal}>
            Add resource
          </Button>
        </div>

        {error ? (
          <InlineNotification
            kind="error"
            title="Resources"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
          />
        ) : null}

        <Tabs
          selectedIndex={selectedIndex}
          onChange={({ selectedIndex }) => setActiveType(resourceTypeTabs[selectedIndex]?.id ?? 'FILESYSTEM')}
        >
          <TabList aria-label="Resource types" contained>
            {resourceTypeTabs.map((tab) => (
              <Tab key={tab.id}>{tab.label}</Tab>
            ))}
          </TabList>
          <TabPanels>
            {resourceTypeTabs.map((tab) => (
              <TabPanel key={tab.id}>
                <div className="settings-page__resources-table-wrap">
                  {loading ? (
                    <div className="settings-page__resources-loading">
                      <InlineLoading description="Loading resources..." />
                    </div>
                  ) : (
                    <>
                      <TableContainer title="">
                        <DataTable
                          rows={activeType === tab.id ? rows : []}
                          headers={headers}
                          render={({ rows, headers, getHeaderProps, getRowProps, getTableProps }) => (
                            <Table {...getTableProps()} size="lg">
                              <TableHead>
                                <TableRow>
                                  {headers.map((header) => (
                                    <TableHeader key={header.key} {...getHeaderProps({ header })}>
                                      {header.header}
                                    </TableHeader>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {rows.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={headers.length}>No resources found for {tab.label}.</TableCell>
                                  </TableRow>
                                ) : (
                                  rows.map((row) => (
                                    <TableRow key={row.id} {...getRowProps({ row })}>
                                      {row.cells.map((cell) => (
                                        <TableCell key={cell.id}>{cell.value}</TableCell>
                                      ))}
                                    </TableRow>
                                  ))
                                )}
                              </TableBody>
                            </Table>
                          )}
                        />
                      </TableContainer>

                      <div className="settings-page__resources-pagination">
                        <Pagination
                          page={page}
                          pageSize={pageSize}
                          pageSizes={[10, 20, 50]}
                          totalItems={activeType === tab.id ? filteredResources.length : 0}
                          onChange={({ page, pageSize }) => {
                            setPage(page);
                            setPageSize(pageSize);
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </div>

      <Modal
        open={resourceModalOpen}
        modalHeading={editingResourceId ? 'Edit resource' : 'Add resource'}
        primaryButtonText={editingResourceId ? 'Save' : 'Create'}
        secondaryButtonText="Cancel"
        onRequestClose={() => setResourceModalOpen(false)}
        onRequestSubmit={handleSaveResource}
      >
        <div className="settings-page__form-grid">
          <TextInput
            id="resource-label"
            labelText="Label"
            value={resourceDraft.label}
            onChange={(e) => setResourceDraft((prev) => ({ ...prev, label: e.target.value }))}
          />

          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ width: '70%' }}>
              <Select
                id="resource-type"
                labelText="Resource Type"
                value={resourceDraft.resourceType}
                onChange={(e) =>
                  setResourceDraft((prev) => {
                    const nextType = e.target.value as ResourceType;
                    return {
                      ...prev,
                      resourceType: nextType,
                      config: deriveConfigWithDefaults(nextType, prev.config),
                    };
                  })
                }
                disabled={Boolean(editingResourceId)}
              >
                {resourceTypeTabs.map((type) => (
                  <SelectItem key={type.id} value={type.id} text={type.label} />
                ))}
              </Select>
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontSize: 12, marginBottom: 8, color: '#c6c6c6' }}>Direction</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Checkbox
                  id="resource-direction-source"
                  labelText="Source"
                  checked={resourceDraft.directionSource}
                  onChange={(_, { checked }) =>
                    setResourceDraft((prev) => ({ ...prev, directionSource: Boolean(checked) }))
                  }
                />
                <Checkbox
                  id="resource-direction-target"
                  labelText="Target"
                  checked={resourceDraft.directionTarget}
                  onChange={(_, { checked }) =>
                    setResourceDraft((prev) => ({ ...prev, directionTarget: Boolean(checked) }))
                  }
                />
              </div>
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, marginBottom: 8, color: '#c6c6c6' }}>Capabilities</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
              {capabilityOptions.map((capability) => (
                <Checkbox
                  key={capability.id}
                  id={`resource-capability-${capability.id}`}
                  labelText={capability.label}
                  checked={resourceDraft.capabilities.includes(capability.id)}
                  onChange={(_, { checked }) =>
                    setResourceDraft((prev) => ({
                      ...prev,
                      capabilities: checked
                        ? [...prev.capabilities, capability.id]
                        : prev.capabilities.filter((item) => item !== capability.id),
                    }))
                  }
                />
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, marginBottom: 8, color: '#c6c6c6' }}>Resource Configuration</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
              {resourceConfigFields.map((field) => (
                <div key={field.key} style={field.fullWidth ? { gridColumn: '1 / -1' } : undefined}>
                  {field.multiline ? (
                    <TextArea
                      id={`resource-config-${field.key}`}
                      labelText={field.label}
                      rows={4}
                      placeholder={field.placeholder}
                      value={resourceDraft.config[field.key] ?? ''}
                      onChange={(e) =>
                        setResourceDraft((prev) => ({
                          ...prev,
                          config: { ...prev.config, [field.key]: e.target.value },
                        }))
                      }
                    />
                  ) : (
                    <TextInput
                      id={`resource-config-${field.key}`}
                      labelText={field.label}
                      placeholder={field.placeholder}
                      type={field.type ?? 'text'}
                      value={resourceDraft.config[field.key] ?? ''}
                      onChange={(e) =>
                        setResourceDraft((prev) => ({
                          ...prev,
                          config: { ...prev.config, [field.key]: e.target.value },
                        }))
                      }
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <TextArea
            id="resource-description"
            labelText="Description"
            rows={4}
            value={resourceDraft.description}
            onChange={(e) => setResourceDraft((prev) => ({ ...prev, description: e.target.value }))}
          />

          <Toggle
            id="resource-enabled"
            labelText="Enabled"
            labelA="Off"
            labelB="On"
            toggled={resourceDraft.enabled}
            onToggle={(value) => setResourceDraft((prev) => ({ ...prev, enabled: value }))}
          />
        </div>
      </Modal>

      <Modal
        open={Boolean(deleteResource)}
        danger
        modalHeading="Delete resource"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestClose={() => setDeleteResource(null)}
        onRequestSubmit={handleDeleteResource}
      >
        <p>
          Are you sure you want to delete resource "{deleteResource?.label}"? This action cannot be undone.
        </p>
      </Modal>

      {submitting ? (
        <div style={{ position: 'fixed', right: 24, bottom: 24, zIndex: 1000 }}>
          <InlineLoading description="Saving resource..." />
        </div>
      ) : null}
    </>
  );
};

export default ResourcesTab;
